import Vue from 'vue'
import { mapState } from 'vuex'
import { Consumer } from '@/app/identity'

/**
 * Mixin to provide address-style-aware translations.
 * 
 * Usage in components:
 * - Use `$ta('key')` instead of `$t('key')` for translations that need formal/informal variants
 * - The translation files should have keys with `_formal` and `_informal` suffixes
 * 
 * Example:
 * Translation keys:
 *   'page.twofactor.enter_code_formal' => 'Voer uw verificatiecode in'
 *   'page.twofactor.enter_code_informal' => 'Voer je verificatiecode in'
 * 
 * Component usage:
 *   {{ $ta('page.twofactor.enter_code') }}
 */
export const AddressStyleTranslationMixin = Vue.extend({
    computed: {
        ...mapState('auth', ['consumer'])
    },
    methods: {
        /**
         * Translate with address style suffix.
         * Appends _formal or _informal to the key based on consumer.addressStyle
         */
        $ta(key: string, values?: Record<string, unknown>): string {
            const consumer = (this as any).consumer as Consumer | undefined
            const suffix = consumer?.hasFormalAddressStyle?.() ? '_formal' : '_informal'
            const styledKey = `${key}${suffix}`
            
            // Try styled key first, fall back to base key if not found
            const result = this.$t(styledKey, values) as string
            if (result === styledKey) {
                // Key not found, try base key
                return this.$t(key, values) as string
            }
            return result
        }
    }
})

/**
 * Helper function to get the address style suffix for a consumer
 */
export function getAddressStyleSuffix(consumer: Consumer | undefined): '_formal' | '_informal' {
    return consumer?.hasFormalAddressStyle?.() ? '_formal' : '_informal'
}

